import { createRouter, createMemoryHistory } from 'vue-router';
import { getInstance } from '@/auth/deAuth';
import { useMainStore } from '@/stores/useMainStore';
import { useAppStore } from '@/stores/useAppStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useSession } from '@/composables/auth/useSession';
import { buildBreadcrumbTrail } from '@/composables/useBreadcrumbs';
import { waitForAuthLoaded } from '@/utils/auth/waitForAuthLoaded';
import { ACCESS_STATES } from '@/utils/auth/accessState';
import { ONBOARDING_STATUS } from '@/utils/auth/agreements';
import { getOnboardingStatus } from '@/utils/auth/currentUserData';
import routes from '@/router/routes';

import { roles } from '@/constants';
import { publicRoutes, allUserRoutes, roleRoutes } from '@/constants/authGuard';

import { getCookie, resetCookie, setCookie } from '@/utils/cookies';
import { getActiveProvider } from './provider';

import { getAuth0Instance } from './index';

const linkValidationRouter = createRouter({ history: createMemoryHistory(), routes });

function isSupportedRoute(path) {
    return linkValidationRouter.resolve(path).matched.length > 0;
}

function errorRouting(to, next) {
    const appStore = useAppStore();
    const hasError = !!appStore.error;

    if (hasError) return true;
    if (to.name === 'error') {
        next('/');
        return false;
    }

    return false;
}

async function checkAuthentication(auth0AuthService, deAuthInstance) {
    // if this user is a role managed by Auth0, check Auth0
    if (auth0AuthService?.isAuthenticated) return true;

    const deAuthenticated = await deAuthInstance?.isAuthenticated;
    return !!deAuthenticated;
}

function handleAuthErrors(authInstance, to) {
    const errorDescription = authInstance?.error?.error_description;
    if (!errorDescription) return null;

    const goingToExpiredPage = to.name === 'password-expired';
    if (!goingToExpiredPage && errorDescription?.includes('PW_EXPIRED')) {
        // Error has been handled. Remove error to restore normal routing.
        authInstance.clearError();
        return { name: 'password-expired' };
    }

    return null;
}

/**
 * Handles situations where a user is navigated to a link inside Career Connect
 * (e.g. `/volunteers/<UUID>`) but they are not authenticated yet.
 * This function will handle saving that link to cookies, and once you are authenticated
 * later, will recover the link and navigate to that link directly.
 * @returns {Boolean} true if `next()` was already called (caller should stop processing this
 * guard); false if no redirect was needed and normal route handling should continue.
 */
const syncDeepLinkRouting = (to, next, isAuthenticated) => {
    const DEEP_LINK_KEY = 'cc-route';

    if (!isAuthenticated) {
        const excludedDeepLinks = ['dashboard', 'login', 'error'];
        if (to.name && !excludedDeepLinks.includes(to.name)) {
            setCookie(DEEP_LINK_KEY, to.path, 0.5);
        }
        next({ name: 'login' });
        return true;
    }

    const deepLink = getCookie(DEEP_LINK_KEY);
    const readyToRedirect = deepLink && !deepLink.includes('login');

    if (readyToRedirect) {
        resetCookie(DEEP_LINK_KEY);

        if (isSupportedRoute(deepLink)) {
            next(deepLink);
            return true;
        }
    }

    return false;
};

const setupBreadcrumbs = (appStore, to) => {
    const pagesExcludedFromBcrumb = ['organization-profile', 'spotlight-content', 'oauth2-authorized', 'login', 'profile-setup', 'dashboard'];

    if (to.name === 'dashboard') {
        appStore.clearNavigationHistory();
        appStore.setBreadcrumbs([]);
        return;
    }

    const pageNeedsBreadcrumb = !pagesExcludedFromBcrumb.includes(to.name);
    if (pageNeedsBreadcrumb) {
        // Add current route to navigation history (with full path to preserve IDs)
        appStore.addToNavigationHistory({
            routeName: to.name,
            fullPath: to.fullPath,
        });

        // Build breadcrumb trail using navigation history
        const breadcrumbs = buildBreadcrumbTrail(to, routes, appStore.mode, appStore.navigationHistory);
        appStore.setBreadcrumbs(breadcrumbs);
    }
};

// Auth0 redirects back to this app's root with ?code=&state= still on the URL, and main.js's
// onRedirect deliberately does no cleanup of its own (see its comment) - this navigation is the
// one place that actually confirms that URL, so it's the right place to drop those params instead
// of confirming `to` as-is and leaving them in the address bar.
function stripAuthCallbackQuery(to) {
    if (!to.query.code || !to.query.state) return null;

    const query = { ...to.query };
    delete query.code;
    delete query.state;
    return { path: to.path, query };
}

// Only users who are allowed in but have not finished onboarding go to profile setup. Anonymous visitors
// (login, public routes) and blocked users (error page) are left to authGuard.
function needsProfileSetup(appStore, to) {
    return appStore.accessState === ACCESS_STATES.ONBOARDING && to.name !== 'profile-setup';
}

export const authGuard = async (to, from, next, authService, deAuthInstance) => {
    const appStore = useAppStore();
    const isAuthenticated = await checkAuthentication(authService, deAuthInstance);

    // Handle error and feature flag routing
    if (errorRouting(to, next)) return next('/error');

    // Public routes (login, oauth2-authorized, password-expired)
    if (publicRoutes.includes(to.name)) {
        return isAuthenticated ? next('/') : next();
    }

    if (syncDeepLinkRouting(to, next, isAuthenticated)) return;

    const { mode } = appStore;

    if (mode === roles.STUDENT_ROLE) {
        return next('/error');
    }

    // The request templates page is being retired for everyone but DE admins - anyone else
    // landing here (e.g. an old bookmark/link) should land on the new request page instead.
    if (to.name === 'request-templates' && mode !== roles.DE_ROLE) {
        return next({ name: 'new-request-v2' });
    }

    const isRouteAllowedForRole = roleRoutes[mode]?.includes(to.name);
    const isSharedRoute = allUserRoutes.includes(to.name);

    if (isRouteAllowedForRole || isSharedRoute) {
        const cleanedTarget = stripAuthCallbackQuery(to);
        return cleanedTarget ? next(cleanedTarget) : next();
    }

    const defaultHomepage = '/';
    return next(defaultHomepage);
};

export const routeGuide = async (to, from, next) => {
    // For URL query manipulation, like what we do in usePagination or useStepperForm, we
    // short-circuit routeGuide as we don't need to do a full page refresh.
    // This prevents any app loading changes or auth/session reloads, even if they are almost instant.
    if (to.name === from.name && to.path === from.path) {
        return next();
    }

    const store = useMainStore();
    const appStore = useAppStore();
    const session = useSession();
    store.updateLoading({ key: 'app', status: true });

    try {
        // If there's already an error set, skip user data loading and proceed to error page
        // This prevents API calls during error page navigation from overwriting the existing error
        if (appStore.error && to.name === 'error') {
            return next();
        }

        setupBreadcrumbs(appStore, to);

        const activeProvider = getActiveProvider();
        const authService = activeProvider === 'de' ? undefined : getAuth0Instance();
        const deAuthInstance = activeProvider === 'auth0' ? undefined : getInstance();

        if (authService) {
            await waitForAuthLoaded(authService);

            // Some Auth0 issues are errors thrown back as URL params. Handle that here.
            const authErrorRedirect = handleAuthErrors(authService, to);
            if (authErrorRedirect) return next(authErrorRedirect);
        }

        // Wait for DE Auth to finish loading
        if (deAuthInstance) {
            await waitForAuthLoaded(deAuthInstance);
        }

        // if authenticated via DE
        if (deAuthInstance?.isAuthenticated) {
            await session.resolveEducatorSession(deAuthInstance);

            if (appStore.active && to.name === 'profile-setup') {
                // active educator cannot route to profile setup confirmation
                return next('/');
            }
        }

        // if Auth0 partner user is authenticated and not a DE admin
        if (authService?.isAuthenticated) {
            await session.resolveAuth0Session(authService);
        }

        // A reload on profile setup lands here with an already-deactivated record, so the URL is the only signal left.
        if (to.name === 'profile-setup' && to.query.returning === 'true') {
            appStore.updateApp({ returningToOnboarding: true });
        }

        if (needsProfileSetup(appStore, to)) {
            // Lets Pendo tell users sent back to setup (e.g. a newly required agreement) from first-timers.
            const query =
                getOnboardingStatus({ appStore, mainStore: store, employeeStore: useEmployeeStore() }) === ONBOARDING_STATUS.RETURNING
                    ? { returning: 'true' }
                    : {};
            return next({ name: 'profile-setup', query });
        }

        return authGuard(to, from, next, authService, deAuthInstance);
    } finally {
        store.updateLoading({ key: 'app', status: false });
    }
};
